import { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { getPayments, createPayment, updatePayment, markPaymentPaid, deletePayment, getCompanies, getBanks } from '../api';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, DollarSign, CheckCircle, AlertCircle } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { format, differenceInDays } from 'date-fns';

const PaymentsPage = ({ user, onLogout }) => {
  const [payments, setPayments] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [deletingPayment, setDeletingPayment] = useState(null);
  const [formData, setFormData] = useState({
    company_id: '',
    account_id: '',
    vendor_name: '',
    amount: '',
    currency: 'INR',
    due_date: '',
    payment_type: 'scheduled',
    remarks: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [paymentsRes, companiesRes, banksRes] = await Promise.all([
        getPayments(),
        getCompanies(),
        getBanks()
      ]);
      setPayments(paymentsRes.data);
      setCompanies(companiesRes.data);
      setBanks(banksRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
        due_date: new Date(formData.due_date).toISOString()
      };

      if (editingPayment) {
        await updatePayment(editingPayment.id, payload);
        toast.success('Payment updated successfully');
      } else {
        await createPayment(payload);
        toast.success('Payment scheduled successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleMarkPaid = async (payment) => {
    try {
      await markPaymentPaid(payment.id);
      toast.success('Payment marked as paid');
      fetchData();
    } catch (error) {
      toast.error('Failed to mark payment as paid');
    }
  };

  const handleDelete = async () => {
    try {
      await deletePayment(deletingPayment.id);
      toast.success('Payment deleted successfully');
      setDeleteDialogOpen(false);
      setDeletingPayment(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete payment');
    }
  };

  const resetForm = () => {
    setFormData({
      company_id: '',
      account_id: '',
      vendor_name: '',
      amount: '',
      currency: 'INR',
      due_date: '',
      payment_type: 'scheduled',
      remarks: ''
    });
    setEditingPayment(null);
  };

  const openEditDialog = (payment) => {
    setEditingPayment(payment);
    setFormData({
      company_id: payment.company_id,
      account_id: payment.account_id,
      vendor_name: payment.vendor_name,
      amount: payment.amount.toString(),
      currency: payment.currency,
      due_date: format(new Date(payment.due_date), 'yyyy-MM-dd'),
      payment_type: payment.payment_type,
      remarks: payment.remarks || ''
    });
    setDialogOpen(true);
  };

  const getCompanyBanks = () => {
    return banks.filter(b => b.company_id === formData.company_id);
  };

  const getBankName = (accountId) => {
    const bank = banks.find(b => b.id === accountId);
    return bank?.account_name || 'Unknown';
  };

  const getDaysUntilDue = (dueDate) => {
    return differenceInDays(new Date(dueDate), new Date());
  };

  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency || 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getDueBadge = (dueDate, status) => {
    if (status === 'paid') {
      return <Badge variant="default" className="bg-green-600">Paid</Badge>;
    }
    const days = getDaysUntilDue(dueDate);
    if (days < 0) {
      return <Badge variant="destructive">Overdue</Badge>;
    }
    if (days <= 7) {
      return <Badge variant="default" className="bg-orange-600">Due Soon</Badge>;
    }
    return <Badge variant="secondary">Pending</Badge>;
  };

  const upcomingPayments = payments.filter(p => p.status === 'pending' && getDaysUntilDue(p.due_date) <= 7);
  const deployablePayments = payments.filter(p => p.status === 'pending' && getDaysUntilDue(p.due_date) > 7);

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Space Grotesk' }} data-testid="payments-page-title">
              Payment Schedule
            </h1>
            <p className="text-slate-600">Manage vendor payments and due dates</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="add-payment-button">
                <Plus className="w-4 h-4" />
                Schedule Payment
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingPayment ? 'Edit Payment' : 'Schedule New Payment'}</DialogTitle>
                <DialogDescription>Enter payment details</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Company</Label>
                  <Select
                    value={formData.company_id}
                    onValueChange={(value) => setFormData({ ...formData, company_id: value, account_id: '' })}
                    required
                  >
                    <SelectTrigger data-testid="payment-company-select">
                      <SelectValue placeholder="Select company" />
                    </SelectTrigger>
                    <SelectContent>
                      {companies.map((company) => (
                        <SelectItem key={company.id} value={company.id}>
                          {company.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Bank Account</Label>
                  <Select
                    value={formData.account_id}
                    onValueChange={(value) => setFormData({ ...formData, account_id: value })}
                    required
                    disabled={!formData.company_id}
                  >
                    <SelectTrigger data-testid="payment-bank-select">
                      <SelectValue placeholder="Select bank account" />
                    </SelectTrigger>
                    <SelectContent>
                      {getCompanyBanks().map((bank) => (
                        <SelectItem key={bank.id} value={bank.id}>
                          {bank.account_name} - {bank.bank_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Vendor Name</Label>
                  <Input
                    data-testid="vendor-name-input"
                    value={formData.vendor_name}
                    onChange={(e) => setFormData({ ...formData, vendor_name: e.target.value })}
                    placeholder="e.g., ABC Supplier"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Amount</Label>
                  <Input
                    data-testid="payment-amount-input"
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Due Date</Label>
                  <Input
                    data-testid="due-date-input"
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Payment Type</Label>
                  <Select
                    value={formData.payment_type}
                    onValueChange={(value) => setFormData({ ...formData, payment_type: value })}
                  >
                    <SelectTrigger data-testid="payment-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="scheduled">Scheduled</SelectItem>
                      <SelectItem value="immediate">Immediate</SelectItem>
                      <SelectItem value="post_dated">Post Dated</SelectItem>
                      <SelectItem value="on_hold">On Hold</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Remarks (Optional)</Label>
                  <Textarea
                    data-testid="payment-remarks-input"
                    value={formData.remarks}
                    onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                    placeholder="Additional notes"
                    rows={3}
                  />
                </div>
                <div className="flex gap-2 pt-4">
                  <Button type="submit" className="flex-1" data-testid="save-payment-button">
                    {editingPayment ? 'Update' : 'Schedule'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setDialogOpen(false);
                      resetForm();
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Alert Cards */}
        <div className="grid gap-4 md:grid-cols-2">
          <Card className="border-orange-200 bg-orange-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5" />
                <div>
                  <p className="font-semibold text-orange-900 mb-1">Upcoming Payments (Next 7 Days)</p>
                  <p className="text-2xl font-bold text-orange-700" data-testid="upcoming-payments-count">
                    {upcomingPayments.length} payments
                  </p>
                  <p className="text-sm text-orange-600 mt-1">
                    Total: {formatCurrency(upcomingPayments.reduce((sum, p) => sum + p.amount, 0), 'INR')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <DollarSign className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="font-semibold text-blue-900 mb-1">Deployable Funds (>7 Days)</p>
                  <p className="text-2xl font-bold text-blue-700" data-testid="deployable-payments-count">
                    {deployablePayments.length} payments
                  </p>
                  <p className="text-sm text-blue-600 mt-1">
                    Consider short-term deployment opportunities
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>All Payments</CardTitle>
            <CardDescription>Complete payment schedule</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-slate-500">Loading...</div>
            ) : payments.length === 0 ? (
              <div className="text-center py-12" data-testid="no-payments-message">
                <DollarSign className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No payments scheduled</p>
                <p className="text-sm text-slate-400 mt-1">Schedule your first payment</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Vendor</TableHead>
                      <TableHead>Bank Account</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Due Date</TableHead>
                      <TableHead>Days Until Due</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments.map((payment) => {
                      const daysUntil = getDaysUntilDue(payment.due_date);
                      return (
                        <TableRow key={payment.id} data-testid={`payment-row-${payment.id}`}>
                          <TableCell className="font-medium">{payment.vendor_name}</TableCell>
                          <TableCell>{getBankName(payment.account_id)}</TableCell>
                          <TableCell className="font-semibold">
                            {formatCurrency(payment.amount, payment.currency)}
                          </TableCell>
                          <TableCell>{format(new Date(payment.due_date), 'dd MMM yyyy')}</TableCell>
                          <TableCell>
                            <span className={daysUntil < 0 ? 'text-red-600' : daysUntil <= 7 ? 'text-orange-600' : 'text-slate-600'}>
                              {daysUntil < 0 ? `${Math.abs(daysUntil)} days overdue` : `${daysUntil} days`}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{payment.payment_type}</Badge>
                          </TableCell>
                          <TableCell>
                            {getDueBadge(payment.due_date, payment.status)}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              {payment.status === 'pending' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleMarkPaid(payment)}
                                  data-testid={`mark-paid-${payment.id}`}
                                  className="text-green-600"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditDialog(payment)}
                                data-testid={`edit-payment-${payment.id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setDeletingPayment(payment);
                                  setDeleteDialogOpen(true);
                                }}
                                data-testid={`delete-payment-${payment.id}`}
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete payment to <strong>{deletingPayment?.vendor_name}</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
};

export default PaymentsPage;